// ANANT FIELDPRO AGENT — Local-First PWA
// All operational data lives in browser localStorage. Server only handles subscription + cascade.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Briefcase, Users, CreditCard, Package, FileText, Clock, PlusCircle,
  Wallet, Loader2, Sparkles, Volume2, MapPin, Download, Trash2, Cloud,
  CheckCircle2, AlertCircle, ChevronRight, PhoneCall, Mic, QrCode, Share2,
  Trophy, Camera, Bell, Copy, RefreshCw,
} from 'lucide-react';
import ATWalletBadge from '../../components/ATWalletBadge';
import ATPayCard from '../../components/ATPayCard';
import VoiceInputHelper2 from '../../components/VoiceInputHelper';
import HoldToSpeak from '../../components/HoldToSpeak';
import api from '../../utils/api';
import { QRCodeSVG } from 'qrcode.react';

// ============ LOCAL STORAGE HELPERS ============
const LS_KEY = 'anant_fp_v1';
const loadLocal = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; } };
const saveLocal = (data) => localStorage.setItem(LS_KEY, JSON.stringify(data));
const useLocalTable = (table, initial = []) => {
  const [rows, setRows] = useState(() => (loadLocal()[table] || initial));
  const setAndSave = (newRows) => {
    setRows(newRows);
    const all = loadLocal(); all[table] = newRows; saveLocal(all);
  };
  return [rows, setAndSave];
};

// Web Speech API — Virtual Soundbox
const speak = (text, lang = 'hi-IN') => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = lang; u.rate = 0.95;
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find((x) => x.lang === lang) || voices.find((x) => x.lang?.startsWith(lang.split('-')[0]));
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
};

// SHA-256 hash for receipt locking
async function sha256(str) {
  try {
    const buf = new TextEncoder().encode(str);
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return String(Date.now()); }
}

// GPS
async function getGPS() {
  try {
    return await new Promise((res, rej) => {
      navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
        (e) => rej(e), { timeout: 8000, maximumAge: 60000 });
    });
  } catch { return null; }
}

export default function AnantFieldPro() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/fieldpro/config'); setCfg(r.data); }
    catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !cfg) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const u = cfg.user || {};
  const active = u.is_active;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-violet-950 via-slate-950 to-cyan-950 text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-violet-400/50 px-4 py-1.5 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white">← Home</Link>
        <div className="flex items-center gap-2">
          <OfflineIndicator />
          <ATWalletBadge compact />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="relative overflow-hidden rounded-3xl border-2 border-violet-400/50 bg-gradient-to-br from-violet-950 via-indigo-900/50 to-cyan-950 p-5 mb-6" data-testid="fp-hero">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none animate-pulse"></div>
          <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-violet-500/25 blur-3xl pointer-events-none"></div>
          <div className="relative flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 border-violet-400 shadow-xl shadow-violet-500/50 flex items-center justify-center bg-gradient-to-br from-violet-500 via-indigo-500 to-cyan-500">
              <Briefcase className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/25 border border-violet-400/40 text-[9px] font-black tracking-widest text-violet-200 mb-1">
                <Sparkles className="w-2.5 h-2.5" /> OFFLINE-FIRST · PWA · ZERO COST
              </div>
              <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-violet-300 via-indigo-200 to-cyan-300 bg-clip-text text-transparent" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>
                ANANT FIELDPRO AGENT
              </h1>
              <div className="text-xs opacity-90 mt-0.5">Pigmy · EMI · Insurance · Sales · 7-Level Cascade · 300 AT/mo</div>
            </div>
          </div>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            <Chip color="violet">💰 Daily Collections</Chip>
            <Chip color="rose">🔁 EMI Recovery</Chip>
            <Chip color="emerald">🛡️ Insurance</Chip>
            <Chip color="amber">📦 B2B Orders</Chip>
            <Chip color="cyan">🔊 Virtual Soundbox</Chip>
          </div>
        </div>

        <SubBanner cfg={cfg} onReload={load} />

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-4" data-testid="fp-tabs">
          <Tab active={tab === 'dashboard'} onClick={() => setTab('dashboard')} tid="fp-tab-dashboard"><Briefcase className="w-3 h-3" /> Dashboard</Tab>
          <Tab active={tab === 'clients'} onClick={() => setTab('clients')} tid="fp-tab-clients"><Users className="w-3 h-3" /> Clients</Tab>
          <Tab active={tab === 'collect'} onClick={() => setTab('collect')} tid="fp-tab-collect"><CreditCard className="w-3 h-3" /> Collect</Tab>
          <Tab active={tab === 'ptp'} onClick={() => setTab('ptp')} tid="fp-tab-ptp"><Clock className="w-3 h-3" /> PTP/EMI</Tab>
          <Tab active={tab === 'orders'} onClick={() => setTab('orders')} tid="fp-tab-orders"><Package className="w-3 h-3" /> B2B</Tab>
          <Tab active={tab === 'reports'} onClick={() => setTab('reports')} tid="fp-tab-reports"><FileText className="w-3 h-3" /> Reports</Tab>
        </div>

        {tab === 'dashboard' && <><ATPayCard label="AT Pay Wallet · FieldPro" /><div className="mt-3" /><DashboardTab cfg={cfg} /></>}
        {tab === 'clients' && <ClientsTab active={active} />}
        {tab === 'collect' && <CollectTab active={active} />}
        {tab === 'ptp' && <PTPTab active={active} />}
        {tab === 'orders' && <OrdersTab active={active} />}
        {tab === 'reports' && <ReportsTab />}

        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-[10px] text-slate-400 leading-relaxed">
          <b className="text-slate-200">Local-First:</b> Your operational data lives on this device only — no server storage. Use &quot;☁️ Backup&quot; in Dashboard to save an encrypted copy for restore. Subscription is stored on server for cascade payouts.
        </div>
      </div>
    </div>
  );
}

const Chip = ({ children, color = 'violet' }) => {
  const map = {
    violet: 'bg-violet-500/15 text-violet-200 border-violet-400/30',
    rose: 'bg-rose-500/15 text-rose-200 border-rose-400/30',
    emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30',
    amber: 'bg-amber-500/15 text-amber-200 border-amber-400/30',
    cyan: 'bg-cyan-500/15 text-cyan-200 border-cyan-400/30',
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black ${map[color]}`}>{children}</span>;
};
const Tab = ({ active, onClick, children, tid }) => (
  <button onClick={onClick} data-testid={tid} className={`px-2 py-2 rounded-xl text-[11px] font-black transition-all flex items-center justify-center gap-1 ${active ? 'bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>{children}</button>
);
const OfflineIndicator = () => {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${online ? 'bg-emerald-500/25 text-emerald-300' : 'bg-red-500/25 text-red-300'}`}>{online ? '🟢 Online' : '🔴 Offline'}</span>;
};

// ============ VOICE INPUT HELPER — Web Speech API (offline-capable in Chrome/Edge/Safari) ============
function VoiceInputHelper({ onDictate, lang = 'hi-IN' }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recRef = useRef(null);
  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input not supported on this browser (Chrome/Edge/Safari recommended)'); return; }
    const rec = new SR();
    rec.lang = lang; rec.interimResults = true; rec.continuous = false;
    rec.onstart = () => { setListening(true); setTranscript(''); };
    rec.onresult = (e) => {
      // Rebuild finals + interim from scratch each event to avoid Chrome's
      // duplicate-emission when it upgrades interim → final for the same segment.
      let final = ''; let interim = '';
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + ' '; else interim += t;
      }
      setTranscript((final + ' ' + interim).replace(/\s+/g, ' ').trim());
    };
    rec.onerror = () => { setListening(false); toast.error('🎤 Voice error, retry'); };
    rec.onend = () => setListening(false);
    recRef.current = rec; rec.start();
  };
  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } };
  const commit = () => {
    if (!transcript) { toast.error('Nothing spoken yet'); return; }
    onDictate?.(transcript);
    setTranscript('');
  };
  return (
    <div className="flex items-center gap-1" data-testid="fp-voice-helper">
      {!listening && !transcript && (
        <button onClick={start} data-testid="fp-voice-start" className="text-[10px] px-2 py-1 rounded bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-black">🎤 Voice</button>
      )}
      {listening && (
        <button onClick={stop} data-testid="fp-voice-stop" className="text-[10px] px-2 py-1 rounded bg-red-500 text-white font-black animate-pulse">⏹ Stop</button>
      )}
      {transcript && !listening && (
        <>
          <div className="text-[10px] px-2 py-1 rounded bg-slate-800 border border-slate-700 max-w-[160px] truncate">{`"${transcript}"`}</div>
          <button onClick={commit} data-testid="fp-voice-commit" className="text-[10px] px-2 py-1 rounded bg-emerald-500 text-black font-black">✓ Use</button>
          <button onClick={() => setTranscript('')} className="text-[10px] px-1.5 py-1 rounded bg-slate-700 text-slate-200">✕</button>
        </>
      )}
    </div>
  );
}


// ============ SUBSCRIPTION BANNER ============
function SubBanner({ cfg, onReload }) {
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState('monthly');
  const [accept, setAccept] = useState(false);
  const [refCode, setRefCode] = useState(cfg.user?.referred_by_username || '');
  const [agentType, setAgentType] = useState(cfg.user?.agent_type || 'MIXED');
  const u = cfg.user || {};
  const active = u.is_active;
  const subscribe = async () => {
    if (!accept) { toast.error('T&C accept karo'); return; }
    setBusy(true);
    try {
      const r = await api.post('/fieldpro/subscribe', { plan: selected, referral_code: refCode || undefined, agent_type: agentType, accept_terms: true });
      // Persist license token locally for offline validation
      const all = loadLocal(); all.__license = { token: r.data.license_token, expiry_at: r.data.expiry_at }; saveLocal(all);
      toast.success(r.data.message);
      await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  if (active) return (
    <div className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-950/40 to-slate-900 p-3 mb-4 flex items-center gap-3" data-testid="fp-sub-active">
      <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-black" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">✨ FieldPro Active · {u.agent_type || 'MIXED'}</div>
        <div className="text-sm font-black">Valid till: <span className="text-emerald-300">{new Date(u.subscription?.expiry_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
      </div>
    </div>
  );
  return (
    <div className="rounded-2xl border-2 border-violet-400/40 bg-gradient-to-br from-violet-950/40 to-slate-900 p-4 mb-4" data-testid="fp-sub-locked">
      <div className="text-[10px] uppercase tracking-widest text-violet-300 font-black mb-2">🔒 Subscribe to unlock all field-agent modules</div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {Object.values(cfg.plans).map((p) => (
          <button key={p.code} onClick={() => setSelected(p.code)} data-testid={`fp-plan-${p.code}`}
            className={`text-left rounded-xl border-2 p-3 transition-all ${selected === p.code ? 'border-violet-400 bg-violet-500/15 shadow-lg shadow-violet-500/40' : 'border-slate-700 bg-slate-800/40'}`}>
            <div className="text-[9px] uppercase tracking-widest text-violet-300 font-black truncate">{p.label}</div>
            <div className="text-lg font-black">{p.price_at.toLocaleString()} AT</div>
            <div className="text-[10px] opacity-70">33% · 7-level cascade</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <select value={agentType} onChange={(e) => setAgentType(e.target.value)} data-testid="fp-agent-type" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="MIXED">Mixed / All-in-One</option>
          <option value="PIGMY">Bank Pigmy / RD Agent</option>
          <option value="EMI">EMI / Recovery Agent</option>
          <option value="INSURANCE">Insurance Agent</option>
          <option value="ORDER">B2B Order Booker</option>
        </select>
        <input placeholder="Referral (sponsor username)" value={refCode} onChange={(e) => setRefCode(e.target.value)} data-testid="fp-ref" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      </div>
      <label className="flex items-start gap-2 text-[10px] mb-2 cursor-pointer">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="fp-accept" className="mt-0.5" />
        <span>I accept — subscription non-refundable, distributed via 33% 7-level cascade (Active-only) + platform.</span>
      </label>
      <button onClick={subscribe} disabled={busy || !accept} data-testid="fp-subscribe" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-violet-500 via-indigo-500 to-cyan-500 text-white text-sm font-black disabled:opacity-40">
        {busy ? 'Processing…' : `Pay ${cfg.plans[selected]?.price_at} AT · Activate`}
      </button>
    </div>
  );
}

// ============ DASHBOARD ============
function DashboardTab({ cfg }) {
  const [ref, setRef] = useState(null);
  const [clients] = useLocalTable('clients', []);
  const [txs] = useLocalTable('transactions', []);
  const [ptps] = useLocalTable('ptp_followups', []);
  useEffect(() => { api.get('/fieldpro/referral/earnings', { params: { days: 30 } }).then((r) => setRef(r.data)).catch(() => {}); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const todayTx = txs.filter((t) => (t.date || '').startsWith(today));
  const todayCollected = todayTx.reduce((s, t) => s + (t.amount || 0), 0);
  const overdue = ptps.filter((p) => p.due_date && p.due_date < today && !p.settled).length;

  const backup = async () => {
    try {
      await api.post('/fieldpro/sync/backup', { payload: loadLocal() });
      toast.success('☁️ Backup saved to cloud');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Backup failed'); }
  };
  const restore = async () => {
    if (!window.confirm('Restore from cloud — this will OVERWRITE local data. Continue?')) return;
    try {
      const r = await api.get('/fieldpro/sync/restore');
      if (r.data?.payload) { saveLocal(r.data.payload); toast.success('Restored — reload page'); setTimeout(() => window.location.reload(), 1500); }
      else toast.info('No cloud backup found');
    } catch { toast.error('Restore failed'); }
  };
  const refLink = `${window.location.origin}/fieldpro?ref=${cfg.user?.referral_code || cfg.user?.username}`;

  return (
    <div className="space-y-3" data-testid="fp-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile label="👥 Clients" v={clients.length} sub="all-time" c="violet" />
        <Tile label="💰 Today Collected" v={`₹${todayCollected.toLocaleString()}`} sub={`${todayTx.length} txs`} c="emerald" />
        <Tile label="⏰ Overdue PTPs" v={overdue} sub="follow-up now" c="rose" />
        <Tile label="🎯 Cascade AT" v={ref?.total_at?.toLocaleString?.() || '0'} sub={`${ref?.directs || 0} directs`} c="amber" />
      </div>

      {ref && (
        <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-950/40 to-slate-900 p-3" data-testid="fp-cascade">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">🎯 7-Level Cascade Earnings (30 days)</div>
          <div className="grid grid-cols-7 gap-1 text-[9px] text-center">
            {cfg.cascade_levels.map((l) => {
              const earned = ref.level_totals?.[`L${l.level}`] || 0;
              return (
                <div key={l.level} className="rounded p-1 border border-slate-700 bg-slate-800/40">
                  <div className="font-black text-fuchsia-300">L{l.level}</div>
                  <div className="opacity-80">{l.pct}%</div>
                  <div className="text-[8px] text-emerald-300 mt-0.5">{earned > 0 ? `+${earned}` : '—'}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap text-[10px]">
            <code className="text-[9px] px-2 py-1 rounded bg-slate-800 text-amber-200 truncate flex-1">{refLink}</code>
            <button onClick={() => { navigator.clipboard.writeText(refLink); toast.success('Copied'); }} className="px-2 py-1 rounded bg-fuchsia-500 text-white text-[10px] font-black">Copy</button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-3">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">☁️ Cloud Backup (optional)</div>
        <div className="text-[10px] opacity-80 mb-2">Your data lives locally on this device. Backup encrypted copy to Anant Cloud for restore across devices.</div>
        <div className="flex gap-2">
          <button onClick={backup} data-testid="fp-backup" className="flex-1 px-3 py-1.5 rounded bg-cyan-500 text-white text-xs font-black flex items-center justify-center gap-1"><Cloud className="w-3 h-3" /> Backup Now</button>
          <button onClick={restore} data-testid="fp-restore" className="flex-1 px-3 py-1.5 rounded bg-slate-700 text-white text-xs font-black">Restore</button>
        </div>
      </div>

      <InsuranceGraceCard />
      <P2PSyncCard active={cfg.user?.is_active} />
      <LeaderboardCard cfg={cfg} />
    </div>
  );
}
const Tile = ({ label, v, sub, c }) => {
  const map = { violet: 'from-violet-500/20 to-violet-900/20 border-violet-400/40',
    emerald: 'from-emerald-500/20 to-emerald-900/20 border-emerald-400/40',
    rose: 'from-rose-500/20 to-rose-900/20 border-rose-400/40',
    amber: 'from-amber-500/20 to-amber-900/20 border-amber-400/40' };
  return (
    <div className={`rounded-xl border p-3 bg-gradient-to-br ${map[c]}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-80 font-black">{label}</div>
      <div className="text-xl font-black">{v}</div>
      <div className="text-[10px] opacity-70">{sub}</div>
    </div>
  );
};

// ============ CLIENTS + FAMILY 360° ============
function ClientsTab({ active }) {
  const [clients, setClients] = useLocalTable('clients', []);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | bday-mo | anni-mo | family-bday-mo | renewal-30 | today
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', address: '', account_no: '', product: 'RD', renewal_date: '', birthday: '', anniversary: '', family_members: [] });
  const [shareTarget, setShareTarget] = useState(null); // client obj
  const save = () => {
    if (!active) { toast.error('Subscription required'); return; }
    if (!form.name) { toast.error('Name required'); return; }
    let rows;
    if (editing) rows = clients.map((c) => c.id === editing ? { ...c, ...form, updated: Date.now() } : c);
    else rows = [{ ...form, id: crypto.randomUUID?.() || String(Date.now()), created: Date.now() }, ...clients];
    setClients(rows); setEditing(null);
    setForm({ name: '', phone: '', address: '', account_no: '', product: 'RD', renewal_date: '', birthday: '', anniversary: '', family_members: [] });
    toast.success(editing ? 'Client updated' : 'Client added');
  };

  const today = new Date();
  const isSameMD = (d, ref = today) => { try { const x = new Date(d); return x.getMonth() === ref.getMonth() && x.getDate() === ref.getDate(); } catch { return false; } };
  const isSameMonth = (d) => { try { return new Date(d).getMonth() === today.getMonth(); } catch { return false; } };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return clients.filter((c) => {
      if (qq) {
        const hay = `${c.name || ''} ${c.phone || ''} ${c.account_no || ''} ${c.address || ''} ${c.product || ''} ${(c.family_members || []).map((m) => m.name).join(' ')}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      if (filter === 'bday-mo') return c.birthday && isSameMonth(c.birthday);
      if (filter === 'anni-mo') return c.anniversary && isSameMonth(c.anniversary);
      if (filter === 'family-bday-mo') return (c.family_members || []).some((m) => m.dob && isSameMonth(m.dob));
      if (filter === 'renewal-30') { if (!c.renewal_date) return false; const d = new Date(c.renewal_date); const days = Math.ceil((d - today) / 86400000); return days >= -60 && days <= 30; }
      if (filter === 'today') {
        return (c.birthday && isSameMD(c.birthday)) || (c.anniversary && isSameMD(c.anniversary)) || (c.family_members || []).some((m) => m.dob && isSameMD(m.dob));
      }
      if (filter && filter.startsWith('prod:')) return c.product === filter.slice(5);
      return true;
    });
  }, [clients, q, filter, today]);

  const eventsToday = [];
  clients.forEach((c) => {
    if (c.birthday && isSameMD(c.birthday)) eventsToday.push({ kind: 'birthday', client: c, who: c.name, msg: `🎂 ${c.name} — Birthday!` });
    if (c.anniversary && isSameMD(c.anniversary)) eventsToday.push({ kind: 'anniversary', client: c, who: c.name, msg: `💍 ${c.name} — Anniversary!` });
    (c.family_members || []).forEach((m) => {
      if (m.dob && isSameMD(m.dob)) eventsToday.push({ kind: 'family_bday', client: c, who: m.name, member: m, msg: `🎂 ${m.name || 'Family'} (${m.rel || ''}) of ${c.name} — Birthday!` });
    });
  });

  const uniqueProducts = Array.from(new Set(clients.map((c) => c.product).filter(Boolean)));

  return (
    <div className="space-y-3" data-testid="fp-clients-tab">
      {eventsToday.length > 0 && (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-500/15 p-2 text-xs font-black text-amber-200" data-testid="fp-events">
          🎉 Family 360° · Cross-sell opportunities today:
          <ul className="mt-1 space-y-0.5 text-[11px]">
            {eventsToday.map((e, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex-1">• {e.msg}</span>
                <button onClick={() => setShareTarget({ ...e.client, _preset: e.kind, _preset_who: e.who })} className="px-1.5 py-0.5 rounded bg-amber-400 text-black text-[9px] font-black">📱 Wish & Share</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-2xl border-2 border-violet-400/40 bg-gradient-to-br from-violet-950/40 to-slate-900 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-violet-300 font-black">👥 Client Master</div>
          <VoiceInputHelper onDictate={(text) => {
            // Parse: "name John phone 9876543210 address ... account 123 product Pigmy"
            const grab = (kw) => {
              const m = text.match(new RegExp(`${kw}\\s+([^,]+?)(?=\\s+(name|phone|address|account|product|birthday|anniversary|renewal)\\s+|$)`, 'i'));
              return m ? m[1].trim() : null;
            };
            const patch = {};
            const name = grab('name'); if (name) patch.name = name;
            const phone = grab('phone'); if (phone) patch.phone = phone.replace(/[^\d]/g, '');
            const address = grab('address'); if (address) patch.address = address;
            const account = grab('account'); if (account) patch.account_no = account;
            const product = grab('product'); if (product) patch.product = product;
            if (Object.keys(patch).length) { setForm((f) => ({ ...f, ...patch })); toast.success(`🎤 Voice: ${Object.keys(patch).join(', ')}`); }
            else toast.error('🎤 Say: "name X phone Y address Z account A product B"');
            // Voice command: "sign" or "save" or "thumb" → trigger save (main agent then shows signature pad if configured)
            if (/\b(save|sign|thumb|confirm)\b/i.test(text)) setTimeout(() => save(), 400);
            if (/\b(skip|cancel)\b/i.test(text)) toast.info('🎤 Skip acknowledged');
          }} />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fp-cli-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="fp-cli-phone" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="fp-cli-addr" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Account No." value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} data-testid="fp-cli-acc" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <select value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option>Pigmy</option><option>RD</option><option>EMI</option><option>Insurance-Life</option><option>Insurance-Health</option><option>Insurance-General</option><option>B2B Order</option>
          </select>
          <label className="flex flex-col text-[9px] opacity-80">
            <span className="mb-0.5">🛡️ Policy/Renewal Date</span>
            <input type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} data-testid="fp-cli-renewal" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
          <label className="flex flex-col text-[9px] opacity-80">
            <span className="mb-0.5">🎂 Birthday</span>
            <input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="fp-cli-bday" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
          <label className="flex flex-col text-[9px] opacity-80">
            <span className="mb-0.5">💍 Anniversary</span>
            <input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} data-testid="fp-cli-anni" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
          <FamilyMembersEditor value={form.family_members} onChange={(fm) => setForm({ ...form, family_members: fm })} />
        </div>
        <button onClick={save} disabled={!active} data-testid="fp-cli-save" className="w-full px-3 py-2 rounded bg-violet-500 text-white text-sm font-black disabled:opacity-40">{editing ? '💾 Update' : '+ Add Client'}</button>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="flex items-center gap-2 mb-2">
          <input placeholder="🔎 Search name / phone / a/c / family…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fp-cli-search" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <span className="text-[10px] opacity-70">{filtered.length}/{clients.length}</span>
        </div>
        <div className="flex gap-1 flex-wrap mb-2 text-[10px]" data-testid="fp-cli-filters">
          {[
            ['all', '📋 All'],
            ['today', '🎉 Today'],
            ['bday-mo', '🎂 Bday this mo'],
            ['anni-mo', '💍 Anniv this mo'],
            ['family-bday-mo', '👨‍👩‍👧 Family Bday'],
            ['renewal-30', '🛡️ Renewal ≤30d'],
          ].map(([id, l]) => (
            <button key={id} onClick={() => setFilter(id)} data-testid={`fp-cli-filter-${id}`} className={`px-2 py-1 rounded font-black ${filter === id ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-300'}`}>{l}</button>
          ))}
          {uniqueProducts.map((p) => (
            <button key={p} onClick={() => setFilter(`prod:${p}`)} data-testid={`fp-cli-filter-prod-${p}`} className={`px-2 py-1 rounded font-black ${filter === `prod:${p}` ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-slate-300'}`}>📦 {p}</button>
          ))}
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {filtered.map((c) => (
            <div key={c.id} className="rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`fp-cli-${c.id}`}>
              <div className="flex items-center gap-2"><b className="flex-1 truncate">{c.name}</b><span className="text-[9px] opacity-70">{c.product}</span></div>
              <div className="text-[10px] opacity-70 flex gap-2 flex-wrap">
                {c.phone && <a href={`tel:${c.phone}`} className="text-emerald-300 flex items-center gap-0.5"><PhoneCall className="w-2.5 h-2.5" />{c.phone}</a>}
                {c.account_no && <span>#{c.account_no}</span>}
                {c.birthday && <span title="Birthday">🎂 {new Date(c.birthday).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>}
                {c.anniversary && <span title="Anniversary">💍 {new Date(c.anniversary).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>}
                {c.family_members?.length > 0 && <span>👨‍👩‍👧 {c.family_members.length}</span>}
              </div>
              <div className="flex gap-1 mt-1 flex-wrap">
                <button onClick={() => { setEditing(c.id); setForm({ ...c }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-[9px] px-2 py-0.5 rounded bg-slate-700 text-slate-200">✏️ Edit</button>
                <button onClick={() => { if (window.confirm('Delete client?')) setClients(clients.filter((x) => x.id !== c.id)); }} className="text-[9px] px-2 py-0.5 rounded bg-red-500/30 text-red-300"><Trash2 className="w-2.5 h-2.5 inline" /> Del</button>
                <button onClick={() => setShareTarget(c)} data-testid={`fp-cli-share-${c.id}`} className="text-[9px] px-2 py-0.5 rounded bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-black ml-auto flex items-center gap-1"><Share2 className="w-2.5 h-2.5" /> Share Card</button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-xs opacity-60 text-center py-3">No clients match this filter</div>}
        </div>
      </div>

      {shareTarget && <ClientShareModal client={shareTarget} onClose={() => setShareTarget(null)} />}
    </div>
  );
}

function FamilyMembersEditor({ value, onChange }) {
  const [name, setName] = useState(''); const [rel, setRel] = useState('Spouse'); const [dob, setDob] = useState('');
  const add = () => { if (!name) return; onChange([...(value || []), { name, rel, dob }]); setName(''); setDob(''); };
  return (
    <div className="col-span-2 rounded border border-slate-700 bg-slate-800/40 p-2">
      <div className="text-[10px] opacity-80 mb-1">👨‍👩‍👧‍👦 Family 360°</div>
      <div className="flex gap-1 mb-1">
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="flex-1 px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]" />
        <select value={rel} onChange={(e) => setRel(e.target.value)} className="px-1 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]">
          <option>Spouse</option><option>Son</option><option>Daughter</option><option>Father</option><option>Mother</option>
        </select>
        <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="px-1 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]" />
        <button onClick={add} className="px-2 py-1 rounded bg-violet-500 text-white text-[10px] font-black">+</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {(value || []).map((m, i) => (
          <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 flex items-center gap-1">
            {m.name} ({m.rel}) {m.dob && <>🎂 {m.dob}</>}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-red-300">✕</button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ============ COLLECT (Pigmy + Denomination Counter + Soundbox + Signature) ============
function CollectTab({ active }) {
  const [clients] = useLocalTable('clients', []);
  const [txs, setTxs] = useLocalTable('transactions', []);
  const [cid, setCid] = useState('');
  const [amt, setAmt] = useState(0);
  const [customAmt, setCustomAmt] = useState('');
  const [txDate, setTxDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState({ n500: 0, n200: 0, n100: 0 });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { client, amount } awaiting signature
  const [editing, setEditing] = useState(null); // tx being edited
  const [editForm, setEditForm] = useState({ amount: 0, notes_txt: '', date: '' });
  useEffect(() => { setAmt(notes.n500 * 500 + notes.n200 * 200 + notes.n100 * 100); }, [notes]);
  const beginCollect = (quick = 0) => {
    if (!active) { toast.error('Subscription required'); return; }
    const client = clients.find((c) => c.id === cid);
    if (!client) { toast.error('Select client'); return; }
    const finalAmt = quick || amt;
    if (finalAmt <= 0) { toast.error('Amount > 0'); return; }
    setPending({ client, amount: finalAmt, notes: { ...notes }, date: txDate });
  };
  const collectCustom = () => {
    const v = parseFloat(customAmt) || 0;
    if (v <= 0) { toast.error('Enter valid custom amount'); return; }
    beginCollect(v);
    setCustomAmt('');
  };
  const finalizeCollect = async (bioData) => {
    // bioData: { data: dataURL, type: 'signature' | 'fingerprint' } | null
    if (!pending) return;
    const { client, amount, notes: n, date } = pending;
    setBusy(true);
    const gps = await getGPS();
    // Combine picked date with current time
    const [yy, mm, dd] = (date || new Date().toISOString().slice(0, 10)).split('-');
    const now = new Date();
    const finalDate = new Date(parseInt(yy), parseInt(mm) - 1, parseInt(dd), now.getHours(), now.getMinutes(), now.getSeconds()).toISOString();
    const tx = {
      id: crypto.randomUUID?.() || String(Date.now()),
      client_id: client.id, client_name: client.name, amount,
      product: client.product, notes: n, gps,
      signature: bioData?.type === 'signature' ? bioData.data : null,
      fingerprint: bioData?.type === 'fingerprint' ? bioData.data : null,
      biometric_type: bioData?.type || null,
      date: finalDate,
    };
    tx.hash = await sha256(JSON.stringify({ ...tx, hash: undefined }));
    setTxs([tx, ...txs]);
    speak(`Anant FieldPro par ${client.name} se ${amount} rupaye prapt hue!`, 'hi-IN');
    const lockLabel = bioData?.type === 'fingerprint' ? '· 👆 fingerprint' : bioData?.type === 'signature' ? '· ✍️ signed' : '';
    toast.success(`✓ ₹${amount} collected from ${client.name} ${lockLabel}`);
    setAmt(0); setNotes({ n500: 0, n200: 0, n100: 0 });
    setBusy(false);
    setPending(null);
  };

  const startEdit = (t) => { setEditing(t.id); setEditForm({ amount: t.amount, notes_txt: t.notes_txt || '', date: t.date?.slice(0, 10) || '' }); };
  const saveEdit = async () => {
    const t = txs.find((x) => x.id === editing);
    if (!t) return;
    const amt2 = parseFloat(editForm.amount);
    if (!(amt2 > 0)) { toast.error('Amount > 0'); return; }
    let newDate = t.date;
    if (editForm.date && editForm.date !== t.date?.slice(0, 10)) {
      const [yy, mm, dd] = editForm.date.split('-');
      const orig = new Date(t.date);
      newDate = new Date(parseInt(yy), parseInt(mm) - 1, parseInt(dd), orig.getHours(), orig.getMinutes(), orig.getSeconds()).toISOString();
    }
    const updated = { ...t, amount: amt2, notes_txt: editForm.notes_txt, date: newDate, edited_at: new Date().toISOString() };
    updated.hash = await sha256(JSON.stringify({ ...updated, hash: undefined }));
    setTxs(txs.map((x) => x.id === editing ? updated : x));
    setEditing(null); toast.success('✓ Transaction updated');
  };
  const deleteTx = (id) => {
    if (!window.confirm('Delete this transaction? (audit trail preserved in PDF exports)')) return;
    setTxs(txs.filter((x) => x.id !== id));
    toast.success('Deleted');
  };

  return (
    <div className="space-y-3" data-testid="fp-collect-tab">
      <div className="rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950/40 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">💰 Daily Collection · Virtual Soundbox 🔊 · ✍️ Sign / 👆 Thumb</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="fp-col-client" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">— Select Client —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.product}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[10px] opacity-90">
            <span className="shrink-0">📅 Date:</span>
            <input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="fp-col-date" max={new Date().toISOString().slice(0, 10)} className="flex-1 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-2">
          {[50, 100, 200, 500, 1000, 2000].map((v) => (
            <button key={v} onClick={() => beginCollect(v)} disabled={busy || !cid} data-testid={`fp-quick-${v}`} className="px-1 py-3 rounded-lg bg-emerald-500 text-black font-black text-sm sm:text-base disabled:opacity-40">₹{v >= 1000 ? `${v / 1000}k` : v}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 mb-2">
          <input type="number" placeholder="Custom ₹ amount" value={customAmt} onChange={(e) => setCustomAmt(e.target.value)} data-testid="fp-custom-amt" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={collectCustom} disabled={busy || !cid || !customAmt} data-testid="fp-custom-collect" className="px-3 py-1.5 rounded bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs font-black disabled:opacity-40">Collect Custom</button>
        </div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-[9px] uppercase font-black text-emerald-300">🎤 Voice Collect (Hold-to-Speak):</span>
          <HoldToSpeak
            label="Bolo · client + amount"
            onTranscript={(text) => {
              const t = text.toLowerCase();
              const amtM = t.match(/(?:amount|rupees|rupaye|rs\.?|₹|rakm)\s*(\d+)/i) || t.match(/\b(\d{2,6})\b/);
              if (amtM) setCustomAmt(amtM[1]);
              const clientM = t.match(/client\s+([\w\s]+?)(?=\s+(?:amount|rupees|rs|₹)\b|$)/i) || t.match(/@?([\w\s]+?)\s+(?:amount|rupees|rs|₹)/i);
              if (clientM) {
                const nm = clientM[1].trim().toLowerCase();
                const match = clients.find((c) => (c.name || '').toLowerCase().includes(nm));
                if (match) setCid(match.id);
              }
              if (amtM || clientM) toast.success(`🎤 Filled${amtM ? ` amount ₹${amtM[1]}` : ''}${clientM ? ` client ${clientM[1]}` : ''}`);
              else toast.error('🎤 Say: "client Ram amount 500"');
              if (/\b(confirm|collect|le\s*lo|bhej\s*do)\b/i.test(t)) setTimeout(() => collectCustom(), 500);
            }}
          />
        </div>
        <div className="text-[10px] opacity-80 mb-1">Or count denominations:</div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {[{ k: 'n500', l: '₹500' }, { k: 'n200', l: '₹200' }, { k: 'n100', l: '₹100' }].map((d) => (
            <div key={d.k} className="rounded bg-slate-800/60 p-2">
              <div className="text-[10px] opacity-80 text-center">{d.l}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => setNotes({ ...notes, [d.k]: Math.max(0, (notes[d.k] || 0) - 1) })} className="w-6 h-6 rounded bg-red-500/25 text-red-300">−</button>
                <input type="number" value={notes[d.k]} onChange={(e) => setNotes({ ...notes, [d.k]: parseInt(e.target.value) || 0 })} data-testid={`fp-note-${d.k}`} className="w-full text-center bg-slate-900 border border-slate-700 rounded text-xs py-1" />
                <button onClick={() => setNotes({ ...notes, [d.k]: (notes[d.k] || 0) + 1 })} className="w-6 h-6 rounded bg-emerald-500/25 text-emerald-300">+</button>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center text-lg font-black text-emerald-300 mb-2" data-testid="fp-col-total">Total: ₹{amt.toLocaleString()}</div>
        <button onClick={() => beginCollect()} disabled={busy || !cid || amt <= 0} data-testid="fp-col-submit" className="w-full px-3 py-2 rounded bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-sm font-black disabled:opacity-40 flex items-center justify-center gap-1"><Mic className="w-4 h-4" /> {busy ? '⏳' : 'Collect · Sign/Thumb & Save'}</button>
      </div>

      {pending && (
        <SignaturePadModal
          client={pending.client}
          amount={pending.amount}
          date={pending.date}
          onDone={finalizeCollect}
          onSkip={() => finalizeCollect(null)}
          onCancel={() => setPending(null)}
        />
      )}

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Recent Collections</div>
          <button onClick={() => downloadPDF('collections', txs)} data-testid="fp-col-pdf" className="text-[10px] px-2 py-1 rounded bg-amber-500 text-black font-black">📄 PDF</button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {txs.slice(0, 40).map((t) => (
            <div key={t.id} className="rounded bg-slate-800/60 px-2 py-1.5 text-[11px]" data-testid={`fp-tx-${t.id}`}>
              {editing === t.id ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <b className="flex-1 truncate">{t.client_name}</b>
                    <input type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid={`fp-tx-edit-amt-${t.id}`} className="w-24 px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-xs" />
                  </div>
                  <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid={`fp-tx-edit-date-${t.id}`} className="w-full px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]" />
                  <input placeholder="Notes (optional)" value={editForm.notes_txt} onChange={(e) => setEditForm({ ...editForm, notes_txt: e.target.value })} className="w-full px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[10px]" />
                  <div className="flex gap-1">
                    <button onClick={saveEdit} data-testid={`fp-tx-save-${t.id}`} className="flex-1 px-2 py-1 rounded bg-emerald-500 text-black text-[10px] font-black">✓ Save</button>
                    <button onClick={() => setEditing(null)} className="px-2 py-1 rounded bg-slate-700 text-slate-200 text-[10px]">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2"><b className="flex-1 truncate">{t.client_name}</b><span className="text-emerald-300 font-black">₹{t.amount}</span></div>
                  <div className="text-[9px] opacity-70 flex gap-2 flex-wrap items-center">
                    <span>{new Date(t.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    {t.gps && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{t.gps.lat.toFixed(3)},{t.gps.lng.toFixed(3)}</span>}
                    {t.signature && <span title="Signed receipt" className="text-cyan-300">✍️ Signed</span>}
                    {t.fingerprint && <span title="Thumb print locked" className="text-emerald-300">👆 Thumb</span>}
                    {t.edited_at && <span className="text-amber-300" title={`Edited ${new Date(t.edited_at).toLocaleString('en-IN')}`}>✏️ Edited</span>}
                    <span className="font-mono opacity-50 truncate">🔒 {t.hash?.slice(0, 10)}</span>
                  </div>
                  {t.notes_txt && <div className="text-[9px] italic opacity-70 mt-0.5">📝 {t.notes_txt}</div>}
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => startEdit(t)} data-testid={`fp-tx-edit-${t.id}`} className="px-2 py-0.5 rounded bg-amber-500/25 text-amber-200 text-[9px] font-black">✏️ Edit</button>
                    <button onClick={() => deleteTx(t.id)} data-testid={`fp-tx-del-${t.id}`} className="px-2 py-0.5 rounded bg-red-500/30 text-red-300 text-[9px] font-black"><Trash2 className="w-2.5 h-2.5 inline" /> Delete</button>
                    {(t.signature || t.fingerprint) && <button onClick={() => shareReceipt(t)} className="ml-auto px-2 py-0.5 rounded bg-cyan-500/25 text-cyan-200 text-[9px] font-black" data-testid={`fp-tx-share-${t.id}`}>Share Receipt</button>}
                  </div>
                </>
              )}
            </div>
          ))}
          {txs.length === 0 && <div className="text-xs opacity-60 text-center py-3">No transactions yet</div>}
        </div>
      </div>
    </div>
  );
}

// ============ SIGNATURE / FINGERPRINT PAD MODAL (touch + mouse) ============
function SignaturePadModal({ client, amount, date, onDone, onSkip, onCancel }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('signature'); // 'signature' | 'fingerprint'
  const [drawing, setDrawing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const clearCanvas = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    setDirty(false);
  }, []);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = c.offsetWidth * 2; c.height = c.offsetHeight * 2;
    const ctx = c.getContext('2d');
    ctx.scale(2, 2);
    ctx.strokeStyle = '#0f172a'; ctx.lineWidth = mode === 'fingerprint' ? 22 : 2.5;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    setDirty(false);
  }, [mode]);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const pt = e.touches?.[0] || e;
    return { x: pt.clientX - r.left, y: pt.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    if (mode === 'fingerprint') {
      // Draw a large soft "ink pad" impression on tap
      const grad = ctx.createRadialGradient(x, y, 4, x, y, 46);
      grad.addColorStop(0, 'rgba(15,23,42,0.85)');
      grad.addColorStop(0.5, 'rgba(15,23,42,0.55)');
      grad.addColorStop(1, 'rgba(15,23,42,0)');
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(x, y, 46, 0, Math.PI * 2); ctx.fill();
      // Add fingerprint-like ridge lines pattern
      ctx.strokeStyle = 'rgba(15,23,42,0.6)'; ctx.lineWidth = 1.2;
      for (let r = 6; r < 42; r += 5) {
        ctx.beginPath();
        for (let a = 0; a < Math.PI * 2; a += 0.35) {
          const jitter = 2 * Math.sin(a * 5 + r);
          const rx = x + (r + jitter) * Math.cos(a);
          const ry = y + (r + jitter) * Math.sin(a) * 0.85;
          if (a === 0) ctx.moveTo(rx, ry); else ctx.lineTo(rx, ry);
        }
        ctx.closePath(); ctx.stroke();
      }
      ctx.fillStyle = '#0f172a';
      setDirty(true);
      return;
    }
    ctx.beginPath(); ctx.moveTo(x, y); setDrawing(true);
  };
  const move = (e) => {
    if (mode === 'fingerprint') return; // fingerprint is single-tap, no drag
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke(); setDirty(true);
  };
  const end = () => setDrawing(false);
  const submit = () => {
    if (!dirty) { toast.error(mode === 'fingerprint' ? 'Please tap thumb impression or press Skip' : 'Please sign or tap Skip'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onDone({ data: dataUrl, type: mode });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3" onClick={onCancel}>
      <div className="bg-slate-900 border-2 border-cyan-400/60 rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="fp-sign-modal">
        <div className="text-xs font-black text-cyan-300 uppercase tracking-widest mb-2">🔒 Tamper-Proof Biometric Lock</div>
        <div className="text-[11px] opacity-90 mb-2">
          <b className="text-cyan-200">{client.name}</b> · <span className="text-emerald-300 font-black">₹{amount.toLocaleString()}</span> · {date ? new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : new Date().toLocaleDateString('en-IN')}
        </div>
        <div className="grid grid-cols-2 gap-1.5 mb-2">
          <button onClick={() => { setMode('signature'); clearCanvas(); }} data-testid="fp-mode-sign" className={`px-3 py-2 rounded text-xs font-black transition ${mode === 'signature' ? 'bg-cyan-500 text-black' : 'bg-slate-800 text-cyan-200'}`}>✍️ Signature</button>
          <button onClick={() => { setMode('fingerprint'); clearCanvas(); }} data-testid="fp-mode-thumb" className={`px-3 py-2 rounded text-xs font-black transition ${mode === 'fingerprint' ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-emerald-200'}`}>👆 Thumb Print</button>
        </div>
        <div className="rounded-lg border-2 border-dashed border-cyan-400/50 bg-white/95 overflow-hidden" style={{ touchAction: 'none' }}>
          <canvas
            ref={canvasRef}
            className={`w-full block ${mode === 'fingerprint' ? 'h-48 cursor-pointer' : 'h-40 cursor-crosshair'}`}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="fp-sign-canvas"
          />
        </div>
        <div className="text-[9px] opacity-70 mt-1 text-center">
          {mode === 'fingerprint'
            ? '👆 Client apna angutha ya first finger touch kare — halka impression bhi chalega (SHA-256 hash)'
            : '✍️ Client finger/pen se sign kare — anti-fraud lock (SHA-256 hash)'}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={clearCanvas} data-testid="fp-sign-clear" className="px-3 py-2 rounded bg-slate-700 text-white text-xs font-black">🧹 Clear</button>
          <button onClick={onSkip} data-testid="fp-sign-skip" className="px-3 py-2 rounded bg-amber-500/25 text-amber-200 text-xs font-black">Skip</button>
          <button onClick={submit} data-testid="fp-sign-save" className="flex-1 px-3 py-2 rounded bg-gradient-to-r from-cyan-500 to-emerald-500 text-black text-sm font-black">✓ Save & Lock</button>
        </div>
      </div>
    </div>
  );
}

// ============ SHARE SIGNED RECEIPT (opens a PDF-style share) ============
async function shareReceipt(tx) {
  try {
    const jsPDF = (await import('jspdf')).default;
    const doc = new jsPDF({ unit: 'pt', format: [420, 600] });
    doc.setFillColor(6, 78, 59); doc.rect(0, 0, 420, 60, 'F');
    doc.setTextColor(240, 253, 244); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('ANANT FIELDPRO · RECEIPT', 210, 38, { align: 'center' });
    doc.setTextColor(20); doc.setFontSize(11);
    let y = 90;
    doc.text(`Client: ${tx.client_name}`, 30, y); y += 18;
    doc.text(`Amount: Rs. ${tx.amount}`, 30, y); y += 18;
    doc.text(`Product: ${tx.product}`, 30, y); y += 18;
    doc.text(`Date: ${new Date(tx.date).toLocaleString('en-IN')}`, 30, y); y += 18;
    if (tx.gps) { doc.text(`GPS: ${tx.gps.lat.toFixed(4)}, ${tx.gps.lng.toFixed(4)}`, 30, y); y += 18; }
    doc.setFontSize(9); doc.text(`Tamper-Lock Hash: ${tx.hash?.slice(0, 32)}...`, 30, y); y += 20;
    if (tx.signature || tx.fingerprint) {
      const bioLabel = tx.fingerprint ? 'Client Thumb Print:' : 'Client Signature:';
      doc.setFontSize(10); doc.text(bioLabel, 30, y); y += 8;
      try { doc.addImage(tx.fingerprint || tx.signature, 'PNG', 30, y, 200, 60); y += 70; } catch { /* skip if invalid */ }
    }
    doc.setFontSize(8); doc.setTextColor(120);
    doc.text('This receipt is digitally signed & hash-locked. Any tampering invalidates the SHA-256 seal.', 30, y);
    const blob = doc.output('blob');
    if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'receipt.pdf', { type: 'application/pdf' })] })) {
      await navigator.share({ files: [new File([blob], 'receipt.pdf', { type: 'application/pdf' })], title: 'FieldPro Receipt' });
    } else {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Receipt opened — share from browser');
    }
  } catch { toast.error('Share failed'); }
}

// ============ PTP / EMI ============
function PTPTab({ active }) {
  const [clients] = useLocalTable('clients', []);
  const [ptps, setPtps] = useLocalTable('ptp_followups', []);
  const [f, setF] = useState({ client_id: '', due_date: '', amount: 0, notes: '' });
  const add = () => {
    if (!active) { toast.error('Subscription required'); return; }
    const c = clients.find((x) => x.id === f.client_id);
    if (!c || !f.due_date || f.amount <= 0) { toast.error('Client + Date + Amount required'); return; }
    setPtps([{ ...f, client_name: c.name, id: crypto.randomUUID?.() || String(Date.now()), settled: false, dpd: 0, created: Date.now() }, ...ptps]);
    setF({ client_id: '', due_date: '', amount: 0, notes: '' });
    toast.success('PTP scheduled');
  };
  const settle = (id) => setPtps(ptps.map((p) => p.id === id ? { ...p, settled: true, settled_at: Date.now() } : p));
  const today = new Date().toISOString().slice(0, 10);
  const bucket = (dueDate) => {
    if (!dueDate || dueDate >= today) return { l: 'UPCOMING', c: 'text-blue-300 bg-blue-500/15 border-blue-400/30' };
    const days = Math.floor((new Date(today) - new Date(dueDate)) / (1000 * 60 * 60 * 24));
    if (days <= 30) return { l: `DPD 1-30 (${days}d)`, c: 'text-amber-300 bg-amber-500/15 border-amber-400/30' };
    if (days <= 60) return { l: `DPD 31-60 (${days}d)`, c: 'text-orange-300 bg-orange-500/15 border-orange-400/30' };
    if (days <= 90) return { l: `DPD 61-90 (${days}d)`, c: 'text-red-300 bg-red-500/15 border-red-400/30' };
    return { l: `DPD 90+ (${days}d)`, c: 'text-red-500 bg-red-500/25 border-red-400/50' };
  };

  return (
    <div className="space-y-3" data-testid="fp-ptp-tab">
      <div className="rounded-2xl border-2 border-rose-400/40 bg-gradient-to-br from-rose-950/40 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2">⏰ EMI · Promise to Pay Calendar</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="fp-ptp-client" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">— Client —</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="fp-ptp-date" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="number" placeholder="Amount ₹" value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })} data-testid="fp-ptp-amt" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <button onClick={add} data-testid="fp-ptp-add" className="w-full px-3 py-2 rounded bg-rose-500 text-white text-sm font-black">+ Add PTP</button>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-2">Follow-ups ({ptps.filter((p) => !p.settled).length})</div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {ptps.filter((p) => !p.settled).map((p) => {
            const b = bucket(p.due_date);
            return (
              <div key={p.id} className={`rounded p-2 border ${b.c}`} data-testid={`fp-ptp-${p.id}`}>
                <div className="flex items-center gap-2 text-[11px]">
                  <b className="flex-1 truncate">{p.client_name}</b>
                  <span className="font-black">₹{p.amount}</span>
                </div>
                <div className="text-[9px] opacity-80 flex items-center gap-2 flex-wrap">
                  <span>📅 {p.due_date}</span><span className="font-black">{b.l}</span>
                  {p.notes && <span className="italic opacity-70">&quot;{p.notes}&quot;</span>}
                </div>
                <div className="mt-1 flex gap-1">
                  <button onClick={() => settle(p.id)} data-testid={`fp-ptp-settle-${p.id}`} className="text-[9px] px-2 py-0.5 rounded bg-emerald-500 text-black font-black">✓ Settle</button>
                  <button onClick={() => { if (window.confirm('Delete?')) setPtps(ptps.filter((x) => x.id !== p.id)); }} className="text-[9px] px-2 py-0.5 rounded bg-red-500/25 text-red-300">✕</button>
                </div>
              </div>
            );
          })}
          {ptps.filter((p) => !p.settled).length === 0 && <div className="text-xs opacity-60 text-center py-3">No pending PTPs</div>}
        </div>
      </div>
    </div>
  );
}

// ============ B2B ORDERS ============
function OrdersTab({ active }) {
  const [products, setProducts] = useLocalTable('products', []);
  const [orders, setOrders] = useLocalTable('orders', []);
  const [clients] = useLocalTable('clients', []);
  const [pForm, setPForm] = useState({ name: '', price: 0, unit: 'pc' });
  const [oForm, setOForm] = useState({ client_id: '', lines: [] });
  const addProduct = () => {
    if (!pForm.name || pForm.price <= 0) { toast.error('Name + Price required'); return; }
    setProducts([{ ...pForm, id: crypto.randomUUID?.() || String(Date.now()) }, ...products]);
    setPForm({ name: '', price: 0, unit: 'pc' });
  };
  const addLine = (p) => setOForm({ ...oForm, lines: [...oForm.lines, { product_id: p.id, name: p.name, price: p.price, qty: 1 }] });
  const total = oForm.lines.reduce((s, l) => s + l.price * l.qty, 0);
  const submitOrder = () => {
    if (!active) { toast.error('Subscription required'); return; }
    const c = clients.find((x) => x.id === oForm.client_id);
    if (!c || oForm.lines.length === 0) { toast.error('Client + Items required'); return; }
    const order = { id: crypto.randomUUID?.() || String(Date.now()), client_id: c.id, client_name: c.name, lines: oForm.lines, total, date: new Date().toISOString() };
    setOrders([order, ...orders]);
    setOForm({ client_id: '', lines: [] });
    speak(`Order booked for ${c.name}, total ${total} rupees`, 'en-IN');
    toast.success(`✓ Order · ₹${total}`);
  };
  return (
    <div className="space-y-3" data-testid="fp-orders-tab">
      <div className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">📦 Offline Product Catalog ({products.length})</div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          <input placeholder="Name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} data-testid="fp-p-name" className="px-1.5 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input type="number" placeholder="₹" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: parseFloat(e.target.value) || 0 })} data-testid="fp-p-price" className="px-1.5 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <button onClick={addProduct} data-testid="fp-p-add" className="px-2 rounded bg-amber-500 text-black font-black text-[11px]">+ Add</button>
        </div>
        <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
          {products.map((p) => (
            <button key={p.id} onClick={() => addLine(p)} data-testid={`fp-p-${p.id}`} className="text-left rounded bg-slate-800/60 p-1.5 text-[10px] hover:bg-slate-800">
              <div className="font-black">{p.name}</div><div className="opacity-70">₹{p.price}/{p.unit}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">📝 New Order</div>
        <select value={oForm.client_id} onChange={(e) => setOForm({ ...oForm, client_id: e.target.value })} data-testid="fp-o-client" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2">
          <option value="">— Shop / Client —</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="text-[10px] opacity-80 mb-1">Click product above to add to order</div>
        <div className="max-h-40 overflow-y-auto space-y-1 mb-2">
          {oForm.lines.map((l, i) => (
            <div key={i} className="flex items-center gap-1 rounded bg-slate-800/60 px-2 py-1 text-[11px]">
              <b className="flex-1 truncate">{l.name}</b>
              <button onClick={() => { const lines = [...oForm.lines]; lines[i] = { ...lines[i], qty: Math.max(1, lines[i].qty - 1) }; setOForm({ ...oForm, lines }); }} className="w-6 h-5 rounded bg-red-500/25 text-red-300">−</button>
              <span className="w-8 text-center font-black">{l.qty}</span>
              <button onClick={() => { const lines = [...oForm.lines]; lines[i] = { ...lines[i], qty: lines[i].qty + 1 }; setOForm({ ...oForm, lines }); }} className="w-6 h-5 rounded bg-emerald-500/25 text-emerald-300">+</button>
              <span className="w-14 text-right font-black text-amber-300">₹{l.price * l.qty}</span>
              <button onClick={() => setOForm({ ...oForm, lines: oForm.lines.filter((_, j) => j !== i) })} className="text-red-300 text-xs">✕</button>
            </div>
          ))}
        </div>
        <div className="text-center text-lg font-black text-amber-300 mb-2">Total: ₹{total.toLocaleString()}</div>
        <button onClick={submitOrder} disabled={!active || oForm.lines.length === 0} data-testid="fp-o-submit" className="w-full px-3 py-2 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-sm font-black disabled:opacity-40">1-Second Order Slip</button>
      </div>

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">Recent Orders ({orders.length})</div>
        <div className="max-h-40 overflow-y-auto space-y-1">
          {orders.slice(0, 15).map((o) => (
            <div key={o.id} className="rounded bg-slate-800/60 px-2 py-1 text-[11px]" data-testid={`fp-o-${o.id}`}>
              <div className="flex items-center gap-2"><b className="flex-1 truncate">{o.client_name}</b><span className="font-black text-amber-300">₹{o.total}</span></div>
              <div className="text-[9px] opacity-70">{new Date(o.date).toLocaleDateString('en-IN')} · {o.lines?.length || 0} items</div>
            </div>
          ))}
          {orders.length === 0 && <div className="text-xs opacity-60 text-center py-3">No orders yet</div>}
        </div>
      </div>
    </div>
  );
}

// ============ REPORTS + PDF ============
async function downloadPDF(kind, rows) {
  try {
    const jsPDF = (await import('jspdf')).default;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    doc.setFillColor(88, 28, 135); doc.rect(0, 0, W, 60, 'F');
    doc.setTextColor(255, 240, 200); doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
    doc.text('ANANT FIELDPRO · ' + kind.toUpperCase(), W / 2, 35, { align: 'center' });
    let y = 90; doc.setTextColor(20); doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')} · ${rows.length} records`, 40, y); y += 20;
    rows.slice(0, 40).forEach((t) => {
      const line = `${new Date(t.date || t.created).toLocaleDateString('en-IN')} · ${t.client_name || t.name || '—'} · ₹${t.amount || t.total || 0}${t.hash ? '  🔒' + t.hash.slice(0, 8) : ''}`;
      doc.text(line, 40, y); y += 14;
      if (y > 780) { doc.addPage(); y = 40; }
    });
    doc.save(`FieldPro-${kind}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF downloaded');
  } catch { toast.error('PDF failed'); }
}
function ReportsTab() {
  const [txs] = useLocalTable('transactions', []);
  const [orders] = useLocalTable('orders', []);
  const [ptps] = useLocalTable('ptp_followups', []);
  const today = new Date().toISOString().slice(0, 10);
  const todayTxs = txs.filter((t) => (t.date || '').startsWith(today));
  const totalToday = todayTxs.reduce((s, t) => s + (t.amount || 0), 0);
  const totalMonth = txs.filter((t) => (t.date || '').slice(0, 7) === today.slice(0, 7)).reduce((s, t) => s + (t.amount || 0), 0);
  return (
    <div className="space-y-3" data-testid="fp-reports-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile label="📅 Today" v={`₹${totalToday.toLocaleString()}`} sub={`${todayTxs.length} txs`} c="emerald" />
        <Tile label="🗓️ This Month" v={`₹${totalMonth.toLocaleString()}`} sub={`${txs.filter((t) => (t.date || '').slice(0, 7) === today.slice(0, 7)).length}`} c="violet" />
        <Tile label="📦 Orders" v={orders.length} sub={`₹${orders.reduce((s, o) => s + (o.total || 0), 0).toLocaleString()}`} c="amber" />
        <Tile label="⏰ Open PTPs" v={ptps.filter((p) => !p.settled).length} sub="follow up" c="rose" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => downloadPDF('DSR-collections', todayTxs)} data-testid="fp-dsr-col" className="px-3 py-2 rounded bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-black text-xs flex items-center justify-center gap-1"><FileText className="w-3 h-3" /> DSR · Collect</button>
        <button onClick={() => downloadPDF('orders', orders)} data-testid="fp-dsr-ord" className="px-3 py-2 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-black text-xs flex items-center justify-center gap-1"><FileText className="w-3 h-3" /> Orders</button>
        <button onClick={() => downloadPDF('ptp', ptps)} data-testid="fp-dsr-ptp" className="px-3 py-2 rounded bg-gradient-to-r from-rose-500 to-pink-500 text-white font-black text-xs flex items-center justify-center gap-1"><FileText className="w-3 h-3" /> PTP</button>
      </div>
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-violet-300 font-black mb-2">Today&apos;s Ledger ({todayTxs.length})</div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {todayTxs.map((t) => (
            <div key={t.id} className="rounded bg-slate-800/60 px-2 py-1 text-[11px]">
              <div className="flex items-center gap-2"><b className="flex-1 truncate">{t.client_name}</b><span className="font-black text-emerald-300">₹{t.amount}</span></div>
              <div className="text-[9px] opacity-70">{new Date(t.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {t.product}</div>
            </div>
          ))}
          {todayTxs.length === 0 && <div className="text-xs opacity-60 text-center py-3">No collections today</div>}
        </div>
      </div>
    </div>
  );
}


// ============ INSURANCE GRACE ALERT CARD (client-side, 15 days) ============
function InsuranceGraceCard() {
  const [clients] = useLocalTable('clients', []);
  const today = useMemo(() => new Date(), []);
  const alerts = useMemo(() => {
    const rows = [];
    clients.forEach((c) => {
      if (!c.renewal_date) return;
      const d = new Date(c.renewal_date);
      if (isNaN(d)) return;
      const days = Math.ceil((d - today) / 86400000);
      if (days <= 30 && days >= -60) rows.push({ ...c, days });
    });
    return rows.sort((a, b) => a.days - b.days);
  }, [clients, today]);

  const sendReminder = (c) => {
    const phone = (c.phone || '').replace(/\D/g, '');
    if (!phone) { toast.error('No phone'); return; }
    const status = c.days < 0 ? `expired ${Math.abs(c.days)} days ago` : c.days === 0 ? 'expires today' : `expires in ${c.days} days`;
    const link = `${window.location.origin}/fieldpro?renew=${encodeURIComponent(c.id)}`;
    const msg = `Namaskar ${c.name} 🙏\n\nAapki ${c.product} policy *${status}* (renewal date: ${c.renewal_date}).\n\nKirpya renewal complete karein taaki grace/lapse na ho.\n\nRenewal link: ${link}\n\n— Anant FieldPro Agent`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (alerts.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-700 bg-slate-900/40 p-3 text-[10px] opacity-70 text-center" data-testid="fp-grace-empty">
        🛡️ Insurance Grace Warning · No renewals in next 30 days
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-rose-400/50 bg-gradient-to-br from-rose-950/50 to-slate-900 p-3" data-testid="fp-grace-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black flex items-center gap-1"><Bell className="w-3 h-3" /> Insurance Grace Warning · Next 30 Days</div>
        <span className="text-[9px] px-2 py-0.5 rounded bg-rose-500 text-white font-black">{alerts.length}</span>
      </div>
      <div className="max-h-60 overflow-y-auto space-y-1">
        {alerts.map((c) => {
          const cls = c.days < 0 ? 'border-red-500 bg-red-500/25' : c.days <= 7 ? 'border-rose-400 bg-rose-500/20' : c.days <= 15 ? 'border-amber-400 bg-amber-500/15' : 'border-slate-600 bg-slate-800/40';
          const label = c.days < 0 ? `EXPIRED ${Math.abs(c.days)}d ago` : c.days === 0 ? 'TODAY' : `in ${c.days} days`;
          return (
            <div key={c.id} className={`rounded-lg border p-2 text-[11px] ${cls}`} data-testid={`fp-grace-${c.id}`}>
              <div className="flex items-center gap-2">
                <b className="flex-1 truncate">{c.name}</b>
                <span className="text-[9px] font-black">{label}</span>
              </div>
              <div className="text-[10px] opacity-80 flex items-center gap-2 flex-wrap">
                <span>{c.product}</span>
                <span>📅 {c.renewal_date}</span>
                {c.phone && <a href={`tel:${c.phone}`} className="text-emerald-300 flex items-center gap-0.5"><PhoneCall className="w-2.5 h-2.5" />{c.phone}</a>}
                <button onClick={() => sendReminder(c)} data-testid={`fp-grace-remind-${c.id}`} className="ml-auto px-2 py-0.5 rounded bg-green-500 text-black text-[9px] font-black">📱 Send WA Reminder</button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[9px] opacity-70 mt-2">⚡ Auto-detects from Clients tab · Add &quot;Renewal Date&quot; while creating/editing client.</div>
    </div>
  );
}

// ============ P2P QR SYNC CARD ============
function P2PSyncCard({ active }) {
  const [mode, setMode] = useState(null); // 'send' | 'receive' | null
  const [shareData, setShareData] = useState(null); // { code, url, expires_at }
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState({ clients: true, products: true, transactions: false, ptp: false });
  const [pasteCode, setPasteCode] = useState('');
  const [importPreview, setImportPreview] = useState(null);

  const buildPayload = () => {
    const all = loadLocal();
    const out = {};
    if (selection.clients) out.clients = all.clients || [];
    if (selection.products) out.products = all.products || [];
    if (selection.transactions) out.transactions = (all.transactions || []).slice(0, 500);
    if (selection.ptp) out.ptp_followups = all.ptp_followups || [];
    return out;
  };

  const createShare = async () => {
    if (!active) { toast.error('Subscription required'); return; }
    const payload = buildPayload();
    const totalItems = Object.values(payload).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0);
    if (totalItems === 0) { toast.error('Select at least one dataset with data'); return; }
    setBusy(true);
    try {
      const r = await api.post('/fieldpro/share/create', { payload, ttl_seconds: 3600, note: `${totalItems} items` });
      const url = `${window.location.origin}/fieldpro?sync=${r.data.code}`;
      setShareData({ code: r.data.code, url, expires_at: r.data.expires_at, items: totalItems, size: r.data.size_bytes });
      toast.success(`✓ Share code created · valid 1 hr`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const fetchAndPreview = async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const r = await api.get(`/fieldpro/share/${code.trim().toUpperCase()}`);
      const p = r.data.payload || {};
      const stats = {
        clients: (p.clients || []).length,
        products: (p.products || []).length,
        transactions: (p.transactions || []).length,
        ptp: (p.ptp_followups || []).length,
      };
      setImportPreview({ payload: p, from: r.data.from_username, stats, code });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid/expired code'); }
    finally { setBusy(false); }
  };

  const applyImport = () => {
    if (!importPreview) return;
    const { payload } = importPreview;
    const all = loadLocal();
    const merge = (arr1 = [], arr2 = []) => {
      const seen = new Map(); [...arr1, ...arr2].forEach((x) => x && x.id && seen.set(x.id, x)); return Array.from(seen.values());
    };
    if (payload.clients) all.clients = merge(all.clients, payload.clients);
    if (payload.products) all.products = merge(all.products, payload.products);
    if (payload.transactions) all.transactions = merge(all.transactions, payload.transactions);
    if (payload.ptp_followups) all.ptp_followups = merge(all.ptp_followups, payload.ptp_followups);
    saveLocal(all);
    toast.success('✓ Merged into local data — reloading…');
    setTimeout(() => window.location.reload(), 1200);
  };

  // Auto-detect ?sync= query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('sync');
    if (code) { setMode('receive'); fetchAndPreview(code); }
  }, []);

  // QR Scanner (dynamic import to keep bundle light)
  const [Scanner, setScanner] = useState(null);
  const openScanner = async () => {
    try {
      const mod = await import('@yudiel/react-qr-scanner');
      setScanner(() => mod.Scanner);
    } catch { toast.error('Scanner not available'); }
  };
  const onScan = (result) => {
    if (!result || result.length === 0) return;
    const text = result[0].rawValue || result[0].text || '';
    try {
      const url = new URL(text);
      const code = url.searchParams.get('sync');
      if (code) { setScanner(null); fetchAndPreview(code); return; }
    } catch { /* not a URL */ }
    // Fallback: treat text as code
    setScanner(null); fetchAndPreview(text.trim().slice(0, 20));
  };

  return (
    <div className="rounded-2xl border-2 border-fuchsia-400/50 bg-gradient-to-br from-fuchsia-950/40 to-slate-900 p-3" data-testid="fp-p2p-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black flex items-center gap-1"><QrCode className="w-3 h-3" /> P2P QR Sync · Zero-Internet Handoff</div>
        <span className="text-[9px] opacity-70">1-hr valid</span>
      </div>
      <div className="text-[10px] opacity-80 mb-2">Do agents apas mein QR scan karke client data swap kar sakte hain — no cloud copy needed.</div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <button onClick={() => { setMode(mode === 'send' ? null : 'send'); setShareData(null); }} data-testid="fp-p2p-send-btn" className={`px-3 py-2 rounded text-xs font-black ${mode === 'send' ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-fuchsia-200'}`}>📤 Send / Show QR</button>
        <button onClick={() => { setMode(mode === 'receive' ? null : 'receive'); setImportPreview(null); }} data-testid="fp-p2p-recv-btn" className={`px-3 py-2 rounded text-xs font-black ${mode === 'receive' ? 'bg-cyan-500 text-black' : 'bg-slate-800 text-cyan-200'}`}>📥 Receive / Scan</button>
      </div>

      {mode === 'send' && !shareData && (
        <div className="space-y-2">
          <div className="text-[10px] opacity-80">Choose what to share:</div>
          <div className="grid grid-cols-2 gap-1 text-[11px]">
            {[['clients', '👥 Clients'], ['products', '📦 Products'], ['transactions', '💰 Transactions (500)'], ['ptp', '⏰ PTP/EMI']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={selection[k]} onChange={(e) => setSelection({ ...selection, [k]: e.target.checked })} data-testid={`fp-p2p-sel-${k}`} />
                <span>{l}</span>
              </label>
            ))}
          </div>
          <button onClick={createShare} disabled={busy} data-testid="fp-p2p-create" className="w-full px-3 py-2 rounded bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-xs font-black disabled:opacity-40">{busy ? 'Creating…' : '🔗 Generate QR + Share Link'}</button>
        </div>
      )}

      {mode === 'send' && shareData && (
        <div className="space-y-2">
          <div className="rounded-xl bg-white p-3 flex justify-center" data-testid="fp-p2p-qr">
            <QRCodeSVG value={shareData.url} size={180} bgColor="#ffffff" fgColor="#0f172a" level="M" includeMargin={true} />
          </div>
          <div className="text-center text-[11px]">
            <div className="font-black text-fuchsia-300 tracking-widest text-lg">{shareData.code}</div>
            <div className="opacity-70">{shareData.items} items · {(shareData.size / 1024).toFixed(1)} KB · expires {new Date(shareData.expires_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <input readOnly value={shareData.url} data-testid="fp-p2p-url" className="flex-1 min-w-0 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[10px] font-mono" />
            <button onClick={() => { navigator.clipboard.writeText(shareData.url); toast.success('Link copied'); }} data-testid="fp-p2p-copy" className="px-2 py-1 rounded bg-fuchsia-500 text-white text-[10px] font-black flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
            <button onClick={() => { const t = `📤 FieldPro Sync\n\nCode: ${shareData.code}\nLink: ${shareData.url}\n\nExpires: 1 hour`; if (navigator.share) navigator.share({ text: t }).catch(() => {}); else window.open(`https://wa.me/?text=${encodeURIComponent(t)}`, '_blank'); }} data-testid="fp-p2p-share" className="px-2 py-1 rounded bg-green-500 text-white text-[10px] font-black flex items-center gap-1"><Share2 className="w-3 h-3" /> Share</button>
          </div>
          <button onClick={() => setShareData(null)} className="w-full px-2 py-1 rounded bg-slate-700 text-slate-300 text-[10px] font-black">↻ New Code</button>
        </div>
      )}

      {mode === 'receive' && !importPreview && (
        <div className="space-y-2">
          <button onClick={openScanner} data-testid="fp-p2p-scan-open" className="w-full px-3 py-2 rounded bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-xs font-black flex items-center justify-center gap-1"><Camera className="w-3 h-3" /> Open Camera Scanner</button>
          {Scanner && (
            <div className="rounded-xl overflow-hidden border-2 border-cyan-400/50 bg-black" style={{ maxHeight: 300 }} data-testid="fp-p2p-scanner">
              <Scanner onScan={onScan} onError={() => { /* silent */ }} constraints={{ facingMode: 'environment' }} />
              <button onClick={() => setScanner(null)} className="w-full px-2 py-1 bg-red-500 text-white text-[10px] font-black">Close</button>
            </div>
          )}
          <div className="text-[10px] opacity-70 text-center">— or paste 10-char code —</div>
          <div className="flex gap-1.5">
            <input placeholder="ABC12XY45Z" value={pasteCode} onChange={(e) => setPasteCode(e.target.value.toUpperCase())} data-testid="fp-p2p-paste" className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono tracking-widest" maxLength={12} />
            <button onClick={() => fetchAndPreview(pasteCode)} disabled={busy || !pasteCode} data-testid="fp-p2p-fetch" className="px-3 py-1.5 rounded bg-cyan-500 text-black text-xs font-black disabled:opacity-40">Fetch</button>
          </div>
        </div>
      )}

      {mode === 'receive' && importPreview && (
        <div className="space-y-2">
          <div className="rounded border border-cyan-400/40 bg-cyan-500/10 p-2 text-[11px]">
            <div className="font-black text-cyan-300">📥 Ready to Merge · from @{importPreview.from}</div>
            <div className="grid grid-cols-2 gap-1 mt-1 text-[10px]">
              <span>👥 Clients: <b>{importPreview.stats.clients}</b></span>
              <span>📦 Products: <b>{importPreview.stats.products}</b></span>
              <span>💰 Txs: <b>{importPreview.stats.transactions}</b></span>
              <span>⏰ PTPs: <b>{importPreview.stats.ptp}</b></span>
            </div>
          </div>
          <div className="text-[9px] opacity-70">Duplicates skipped by ID · Local data protected.</div>
          <div className="flex gap-2">
            <button onClick={() => setImportPreview(null)} className="flex-1 px-2 py-1.5 rounded bg-slate-700 text-slate-300 text-[10px] font-black">Cancel</button>
            <button onClick={applyImport} data-testid="fp-p2p-apply" className="flex-1 px-2 py-1.5 rounded bg-gradient-to-r from-cyan-500 to-emerald-500 text-black text-xs font-black">✓ Merge Now</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ COMMISSION LEADERBOARD CARD ============
function LeaderboardCard({ cfg }) {
  const [board, setBoard] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/fieldpro/leaderboard', { params: { days } }); setBoard(r.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const share = () => {
    if (!board) return;
    const period = days === 1 ? 'Today' : days === 7 ? 'This Week' : `${days} days`;
    const lines = [`🏆 *FieldPro Commission Leaderboard — ${period}*`, ''];
    (board.leaderboard || []).slice(0, 10).forEach((r, i) => {
      const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
      lines.push(`${medal} @${r.username} — ${r.earned_at.toLocaleString()} AT (${r.count} tx)`);
    });
    lines.push('', `📊 My Rank: ${board.my_rank || '—'} · My AT: ${board.my_earned_at.toLocaleString()}`);
    lines.push('', `Join Anant FieldPro — 300 AT/mo + 7-Level Cascade`);
    lines.push(`${window.location.origin}/fieldpro?ref=${cfg.user?.username}`);
    const text = lines.join('\n');
    if (navigator.share) navigator.share({ text }).catch(() => {});
    else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-950/40 via-slate-900 to-yellow-950/30 p-3" data-testid="fp-leaderboard-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1"><Trophy className="w-3 h-3" /> Commission Leaderboard</div>
        <div className="flex gap-1">
          {[1, 7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`fp-lb-${d}d`} className={`text-[9px] px-2 py-0.5 rounded font-black ${days === d ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}>{d === 1 ? 'Today' : d === 7 ? 'Week' : 'Month'}</button>
          ))}
          <button onClick={load} disabled={loading} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300"><RefreshCw className={`w-2.5 h-2.5 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      {board && board.leaderboard.length === 0 && (
        <div className="text-[11px] opacity-70 text-center py-3">Koi cascade earning nahi hui abhi · Apna referral link share karo!</div>
      )}
      {board && board.leaderboard.length > 0 && (
        <>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {board.leaderboard.slice(0, 10).map((r, i) => {
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
              const bg = r.is_you ? 'border-cyan-400 bg-cyan-500/20' : i < 3 ? 'border-amber-400/40 bg-amber-500/10' : 'border-slate-700 bg-slate-800/40';
              return (
                <div key={r.user_id} className={`rounded-lg border p-2 flex items-center gap-2 text-[11px] ${bg}`} data-testid={`fp-lb-row-${i}`}>
                  <div className="text-lg w-8 text-center font-black">{medal}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black truncate">@{r.username} {r.is_you && <span className="text-[9px] px-1 rounded bg-cyan-400 text-black">YOU</span>}</div>
                    <div className="text-[9px] opacity-70">{r.count} cascade tx</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-amber-300">{r.earned_at.toLocaleString()}</div>
                    <div className="text-[9px] opacity-70">AT</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 rounded-lg bg-slate-800/60 border border-slate-700 p-2 flex items-center justify-between text-[11px]">
            <div>
              <div className="text-[9px] uppercase tracking-widest opacity-70 font-black">Your Standing</div>
              <div className="font-black">Rank {board.my_rank || '—'} · {board.my_earned_at.toLocaleString()} AT</div>
            </div>
            <button onClick={share} data-testid="fp-lb-share" className="px-3 py-1.5 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-xs font-black flex items-center gap-1"><Share2 className="w-3 h-3" /> Share Card</button>
          </div>
        </>
      )}
    </div>
  );
}

// ============ CLIENT SHARE MODAL — attractive social-media card + custom message ============
const SHARE_TEMPLATES = [
  { id: 'birthday',      label: '🎂 Birthday',       accent: '#f472b6', bg: ['#831843','#db2777','#f97316'], emoji: '🎂🎉', tag: 'HAPPY BIRTHDAY',
    msg: (c, who) => `🎂 Happy Birthday, ${who || c.name}!\n\nAnant FieldPro parivaar ki taraf se aapko din bhar ki dher saari khushiyaan, sehat aur pyaar. May all your dreams come true! 🎉` },
  { id: 'anniversary',   label: '💍 Anniversary',    accent: '#fbbf24', bg: ['#7c2d12','#f59e0b','#dc2626'], emoji: '💍✨', tag: 'HAPPY ANNIVERSARY',
    msg: (c, who) => `💍 Happy Anniversary, ${who || c.name} ji!\n\nAapki jodi hamesha khush aur salamat rahe. Iss khaas din par Anant FieldPro parivaar ki dher saari shubhkaamnayein. 🌹✨` },
  { id: 'festival',      label: '🪔 Festival',       accent: '#fb923c', bg: ['#7c2d12','#ea580c','#facc15'], emoji: '🪔🎊', tag: 'FESTIVAL GREETINGS',
    msg: (c) => `🪔 Namaskar ${c.name} ji,\n\nAapko aur aapke parivaar ko is shubh avsar ki dher saari shubhkaamnayein. Anant Web Portal parivaar aapki tarakki aur khushi ki dua karta hai. 🙏` },
  { id: 'renewal',       label: '🛡️ Renewal',         accent: '#ef4444', bg: ['#450a0a','#dc2626','#7f1d1d'], emoji: '🛡️⏰', tag: 'RENEWAL REMINDER',
    msg: (c) => `🛡️ Namaskar ${c.name} ji,\n\nAapki ${c.product || 'policy'} renewal date paas aa rahi hai${c.renewal_date ? ` (${c.renewal_date})` : ''}. Grace period miss na ho, kripya jaldi renewal karvayein.\n\n— Anant FieldPro Agent` },
  { id: 'welcome',       label: '🙏 Welcome',         accent: '#22d3ee', bg: ['#082f49','#0891b2','#1e3a8a'], emoji: '🙏🌟', tag: 'WELCOME ABOARD',
    msg: (c) => `🙏 Swagat hai ${c.name} ji!\n\nAap ab Anant FieldPro parivaar ka hissa hain. Aapki savings aur suraksha meri zimmedari. Koi bhi seva ke liye seedha WhatsApp karein. 🌟` },
  { id: 'thanks',        label: '🙌 Thanks',          accent: '#10b981', bg: ['#052e16','#10b981','#022c22'], emoji: '🙌💚', tag: 'DHANYAWAD',
    msg: (c) => `🙌 Dhanyawad ${c.name} ji!\n\nAapke bharose ke liye Anant FieldPro parivaar aabhari hai. Aapki loyalty humari sabse badi taakat hai. 💚` },
];

function ClientShareModal({ client, onClose }) {
  const initial = SHARE_TEMPLATES.find((t) => t.id === client._preset) || SHARE_TEMPLATES[0];
  const [tpl, setTpl] = useState(initial);
  const [msg, setMsg] = useState(() => initial.msg(client, client._preset_who));
  const [subtitle, setSubtitle] = useState(client._preset_who || client.name || '');
  const canvasRef = useRef(null);

  useEffect(() => { setMsg(tpl.msg(client, client._preset_who)); }, [tpl, client]);

  const draw = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const W = 1080, H = 1080;
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, W, H);
    tpl.bg.forEach((col, i) => g.addColorStop(i / (tpl.bg.length - 1 || 1), col));
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    // Decorative rings
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.arc(W * (i % 2 ? 0.1 : 0.9), H * (i * 0.18 + 0.1), 60 + i * 22, 0, Math.PI * 2);
      ctx.fillStyle = `${tpl.accent}22`; ctx.fill();
    }
    // Overlay
    const og = ctx.createLinearGradient(0, 0, 0, H);
    og.addColorStop(0, 'rgba(0,0,0,0.15)'); og.addColorStop(1, 'rgba(0,0,0,0.75)');
    ctx.fillStyle = og; ctx.fillRect(0, 0, W, H);

    // Big emoji
    ctx.textAlign = 'center';
    ctx.font = '260px "Helvetica","Arial",sans-serif';
    ctx.fillText(tpl.emoji, W / 2, H * 0.42);

    // Tag
    ctx.fillStyle = tpl.accent; ctx.font = 'bold 52px "Helvetica","Arial",sans-serif';
    ctx.fillText(tpl.tag, W / 2, H * 0.58);

    // Subtitle (name)
    ctx.fillStyle = '#fff'; ctx.font = 'bold 96px "Georgia","Times New Roman",serif';
    const wrap = (text, maxW) => {
      const words = String(text || '').split(' '); const lines = []; let cur = '';
      words.forEach((w) => { const t = cur ? cur + ' ' + w : w; if (ctx.measureText(t).width > maxW) { if (cur) lines.push(cur); cur = w; } else cur = t; });
      if (cur) lines.push(cur); return lines.slice(0, 3);
    };
    const sublines = wrap(subtitle || client.name, W - 100);
    sublines.forEach((ln, i) => ctx.fillText(ln, W / 2, H * 0.68 + i * 100));

    // Bottom message preview (first 2 lines)
    ctx.fillStyle = '#fff'; ctx.font = '400 34px "Helvetica","Arial",sans-serif';
    const preview = msg.split('\n').filter(Boolean).slice(0, 2).join(' · ');
    const p2 = wrap(preview, W - 160);
    p2.slice(0, 2).forEach((ln, i) => ctx.fillText(ln, W / 2, H * 0.88 + i * 44));

    // Watermark
    ctx.fillStyle = tpl.accent; ctx.fillRect(0, H - 70, W, 70);
    ctx.fillStyle = '#fff'; ctx.font = 'bold 40px "Helvetica","Arial",sans-serif';
    ctx.fillText('ANANT FIELDPRO · With Love', W / 2, H - 24);
  }, [tpl, subtitle, client, msg]);

  useEffect(() => { draw(); }, [draw]);

  const shareTo = async (channel) => {
    try {
      const blob = await new Promise((r) => canvasRef.current.toBlob(r, 'image/png'));
      const file = new File([blob], `wish-${tpl.id}.png`, { type: 'image/png' });
      if (channel === 'native' && navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: msg, title: tpl.tag });
        return;
      }
      if (channel === 'download') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `wish-${tpl.id}.png`; a.click();
        URL.revokeObjectURL(url); toast.success('Card downloaded');
        return;
      }
      if (channel === 'whatsapp') {
        const phone = (client.phone || '').replace(/\D/g, '');
        const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
        // Also download the card so user can attach in WA
        const durl = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = durl; a.download = `wish-${tpl.id}.png`; a.click();
        URL.revokeObjectURL(durl);
        window.open(url, '_blank');
        toast.success('WA opened · card downloaded (attach it)');
        return;
      }
      if (channel === 'facebook' || channel === 'twitter') {
        const durl = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = durl; a.download = `wish-${tpl.id}.png`; a.click();
        URL.revokeObjectURL(durl);
        const shareUrl = channel === 'facebook'
          ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(msg)}`
          : `https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`;
        window.open(shareUrl, '_blank');
        toast.success('Card downloaded · social page opened');
        return;
      }
      if (channel === 'copy') {
        await navigator.clipboard.writeText(msg);
        toast.success('Message copied');
      }
    } catch { toast.error('Share failed'); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start justify-center p-2 overflow-y-auto" onClick={onClose}>
      <div className="bg-slate-900 border-2 border-fuchsia-400 rounded-2xl p-3 max-w-md w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="fp-share-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-fuchsia-300 uppercase tracking-widest">📱 Share to Social · {client.name}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 mb-2">
          {SHARE_TEMPLATES.map((t) => (
            <button key={t.id} onClick={() => setTpl(t)} data-testid={`fp-share-tpl-${t.id}`}
              className={`text-[9px] px-1 py-1.5 rounded-lg border-2 font-black transition-all ${tpl.id === t.id ? 'border-fuchsia-400 scale-105' : 'border-slate-700 opacity-70'}`}
              style={{ background: `linear-gradient(135deg, ${t.bg.join(', ')})`, color: '#fff', minHeight: 44 }}>{t.label}</button>
          ))}
        </div>
        <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} data-testid="fp-share-name" placeholder="Name on card" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2" />
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="fp-share-msg" rows={4} placeholder="Custom message" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2" />
        <div className="rounded-lg overflow-hidden border-2 border-fuchsia-400/40 bg-black mb-2 flex justify-center">
          <canvas ref={canvasRef} className="max-h-[36vh] w-auto block" />
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <button onClick={() => shareTo('whatsapp')} data-testid="fp-share-wa" className="px-2 py-2 rounded bg-green-500 text-white text-[10px] font-black">💬 WhatsApp</button>
          <button onClick={() => shareTo('facebook')} data-testid="fp-share-fb" className="px-2 py-2 rounded bg-blue-600 text-white text-[10px] font-black">📘 Facebook</button>
          <button onClick={() => shareTo('twitter')} data-testid="fp-share-tw" className="px-2 py-2 rounded bg-slate-800 text-white text-[10px] font-black">𝕏 Twitter</button>
          <button onClick={() => shareTo('native')} data-testid="fp-share-native" className="px-2 py-2 rounded bg-fuchsia-500 text-white text-[10px] font-black flex items-center justify-center gap-1"><Share2 className="w-3 h-3" />More</button>
          <button onClick={() => shareTo('copy')} data-testid="fp-share-copy" className="px-2 py-2 rounded bg-slate-700 text-white text-[10px] font-black flex items-center justify-center gap-1"><Copy className="w-3 h-3" />Copy Text</button>
          <button onClick={() => shareTo('download')} data-testid="fp-share-dl" className="px-2 py-2 rounded bg-amber-500 text-black text-[10px] font-black">💾 PNG</button>
        </div>
      </div>
    </div>
  );
}

